import {IssueOpenedIcon, PlusCircleIcon, TriangleDownIcon, PlusIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Button, ButtonGroup, IconButton, TextInput} from '@primer/react'
import {useCallback, useState} from 'react'
import {addSubIssueMutation} from './mutations/add-sub-issue-mutation'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
import {useToastContext} from '@github-ui/toast/ToastContext'
import type {addSubIssueMutation$data} from './mutations/__generated__/addSubIssueMutation.graphql'
import {SubIssuesCreateDialog} from './SubIssuesCreateDialog'
import type {AddSubIssueButtonGroup$key} from './__generated__/AddSubIssueButtonGroup.graphql'

export function AddSubIssueButtonGroup({
  issue,
  onCreateSubIssueSuccess,
}: {
  issue: AddSubIssueButtonGroup$key
  onCreateSubIssueSuccess?: () => void
}) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [addingSubIssue, setAddingSubIssue] = useState(false)
  const environment = useRelayEnvironment()

  const data = useFragment(
    graphql`
      fragment AddSubIssueButtonGroup on Issue {
        id
        ...SubIssuesCreateDialog
      }
    `,
    issue,
  )

  const addSubIssue = useCallback(
    (url: string, onComplete?: (response: addSubIssueMutation$data) => void, onError?: (error: Error) => void) => {
      addSubIssueMutation({
        environment,
        input: {
          issueId: data.id,
          subIssueUrl: url,
        },
        onError: error => {
          onError?.(error)
        },
        onCompleted: response => {
          onComplete?.(response)
        },
      })
    },
    [data.id, environment],
  )
  return (
    <Box sx={{gap: 2, display: 'flex'}}>
      <ButtonGroup>
        <Button onClick={() => setCreateDialogOpen(true)}>Create sub-issue</Button>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={TriangleDownIcon} aria-label="View more sub-issue options" />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay width="small">
            <ActionList>
              <ActionList.Group>
                <ActionList.Item role="button" onSelect={() => setCreateDialogOpen(true)}>
                  <ActionList.LeadingVisual>
                    <PlusCircleIcon />
                  </ActionList.LeadingVisual>
                  Create sub-issue
                </ActionList.Item>
                <ActionList.Item role="button" onSelect={() => setAddingSubIssue(true)}>
                  <ActionList.LeadingVisual>
                    <IssueOpenedIcon />
                  </ActionList.LeadingVisual>
                  Add existing issue
                </ActionList.Item>
              </ActionList.Group>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </ButtonGroup>
      {addingSubIssue && <AddSubIssueInput addSubIssue={addSubIssue} hide={() => setAddingSubIssue(false)} />}
      <SubIssuesCreateDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        issue={data}
        onCreateSuccess={({createMore}) => {
          if (!createMore) {
            setCreateDialogOpen(false)
          }
          onCreateSubIssueSuccess?.()
        }}
      />
    </Box>
  )
}

// AddSubIssueInput is a temporary stop-gap until we implement an issue picker with ItemPicker, so we abstract as much
// logic that won't be used in the final implementation to this component.
const AddSubIssueInput = ({
  addSubIssue,
  hide,
}: {
  addSubIssue: (url: string, onComplete?: () => void, onError?: (error: Error) => void) => void
  hide: () => void
}) => {
  const {addToast} = useToastContext()
  const [url, setUrl] = useState('')
  const addByUrl = useCallback(() => {
    addSubIssue(
      url,
      () => setUrl(''),
      error => {
        addToast({
          type: 'error',
          message: error.message,
        })
      },
    )
  }, [url, addSubIssue, addToast])

  return (
    <TextInput
      sx={{width: '100%'}}
      aria-label="Add a sub-issue by its URL"
      placeholder="Insert URL for subissue"
      value={url}
      onChange={e => setUrl(e.target.value)}
      onKeyDown={e => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === 'Enter') {
          e.preventDefault()
          addByUrl()
        }
      }}
      trailingAction={
        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
          <IconButton
            size="small"
            variant="invisible"
            onClick={() => {
              hide()
              setUrl('')
            }}
            icon={XIcon}
            aria-label="Close URL input"
          />
          <IconButton size="small" variant="invisible" onClick={addByUrl} icon={PlusIcon} aria-label="Add Issue" />
        </Box>
      }
    />
  )
}

try{ AddSubIssueButtonGroup.displayName ||= 'AddSubIssueButtonGroup' } catch {}
try{ AddSubIssueInput.displayName ||= 'AddSubIssueInput' } catch {}